<template>
  <div class="period-statistics">
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <template v-if="stats">
      <!-- Блок 1: Статусы лояльности -->
      <v-card
        class="mb-4 pa-4"
        :outlined="embedded"
      >
        <div class="text-subtitle-1 font-weight-bold mb-3">
          {{ isSingleClient ? 'Статус участника' : 'Статусы участников' }}
        </div>

        <div class="text-body-2 font-weight-medium mb-1">
          На текущий момент
        </div>
        <div class="text--secondary text-body-2 mb-3">
          Независимо от выбранного периода
        </div>
        <v-row
          dense
          class="mb-6"
        >
          <v-col
            v-for="tier in loyaltyTierCards"
            :key="`current-${tier.key}`"
            cols="6"
            sm="3"
          >
            <v-card
              outlined
              class="pa-3"
            >
              <div class="text-body-2 text--secondary">
                {{ tier.label }}
              </div>
              <router-link
                v-if="!isSingleClient && stats.loyalty_tiers[tier.key] > 0"
                :to="clientsFilterRoute(tier.key)"
                class="text-h6 font-weight-bold loyalty-tier-count loyalty-tier-link"
                :class="`loyalty-tier-count--${tier.key}`"
              >
                {{ stats.loyalty_tiers[tier.key] }}
              </router-link>
              <div
                v-else
                class="text-h6 font-weight-bold loyalty-tier-count"
                :class="`loyalty-tier-count--${tier.key}`"
              >
                {{ stats.loyalty_tiers[tier.key] }}
              </div>
            </v-card>
          </v-col>
        </v-row>

        <div class="text-body-2 font-weight-medium mb-1">
          За выбранный период
        </div>
        <div class="text--secondary text-body-2 mb-3">
          {{ stats.period.label }}.
          <template v-if="isSingleClient">
            Для одного участника в каждом статусе — 0 или 1.
          </template>
          <template v-else>
            При периоде в несколько кварталов один игрок может учитываться в разных статусах;
            сумма по статусам может превышать число уникальных участников.
          </template>
        </div>

        <v-row dense>
          <v-col
            v-for="tier in loyaltyTierCards"
            :key="`period-${tier.key}`"
            cols="6"
            sm="3"
          >
            <v-card
              outlined
              class="pa-3"
            >
              <div class="text-body-2 text--secondary">
                {{ tier.label }}
              </div>
              <div
                class="text-h6 font-weight-bold loyalty-tier-count"
                :class="`loyalty-tier-count--${tier.key}`"
              >
                {{ periodLoyaltyCounts[tier.key] }}
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card>

      <!-- Блок 2: Подарки -->
      <v-card
        class="mb-4 pa-4"
        :outlined="embedded"
      >
        <div class="text-subtitle-1 font-weight-bold mb-3">
          Полученные подарки за период
        </div>

        <div
          v-if="stats.gifts.total === 0"
          class="text--secondary"
        >
          Подарков за выбранный период нет
        </div>
        <template v-else>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <div class="text-body-2 font-weight-medium mb-2">
                Физические подарки
              </div>
              <v-simple-table
                v-if="stats.gifts.physical.length"
                dense
              >
                <template #default>
                  <thead>
                    <tr>
                      <th>Подарок</th>
                      <th class="text-right">
                        Кол-во
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in stats.gifts.physical"
                      :key="item.gift_id"
                    >
                      <td>
                        <span
                          class="href"
                          @click="openGiftDetails(item)"
                        >{{ item.title }}</span>
                      </td>
                      <td class="text-right">
                        <span
                          class="href"
                          @click="openGiftDetails(item)"
                        >{{ item.count }}</span>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <div
                v-else
                class="text--secondary text-body-2"
              >
                —
              </div>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <div class="text-body-2 font-weight-medium mb-2">
                Виртуальные подарки (занятия и пр.)
              </div>
              <v-simple-table
                v-if="stats.gifts.virtual.length"
                dense
              >
                <template #default>
                  <thead>
                    <tr>
                      <th>Подарок</th>
                      <th class="text-right">
                        Кол-во
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in stats.gifts.virtual"
                      :key="item.gift_id"
                    >
                      <td>
                        <span
                          class="href"
                          @click="openGiftDetails(item)"
                        >{{ item.title }}</span>
                      </td>
                      <td class="text-right">
                        <span
                          class="href"
                          @click="openGiftDetails(item)"
                        >{{ item.count }}</span>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <div
                v-else
                class="text--secondary text-body-2"
              >
                —
              </div>
            </v-col>
          </v-row>
          <div class="mt-3 text-body-2">
            Итого подарков: <strong>{{ stats.gifts.total }}</strong>
          </div>
        </template>
      </v-card>

      <!-- Блок 3: Бесплатные отмены -->
      <v-card
        class="mb-4 pa-4"
        :outlined="embedded"
      >
        <div class="text-subtitle-1 font-weight-bold mb-3">
          Бесплатные отмены за период (привилегия лояльности)
        </div>
        <v-simple-table dense>
          <template #default>
            <thead>
              <tr>
                <th>Тип</th>
                <th class="text-right">
                  Кол-во
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <span
                    v-if="stats.free_cancelings.activity > 0"
                    class="href"
                    @click="openFreeCancelingDetails('activity')"
                  >Групповые занятия</span>
                  <span v-else>Групповые занятия</span>
                </td>
                <td class="text-right">
                  <span
                    v-if="stats.free_cancelings.activity > 0"
                    class="href"
                    @click="openFreeCancelingDetails('activity')"
                  >{{ stats.free_cancelings.activity }}</span>
                  <span v-else>{{ stats.free_cancelings.activity }}</span>
                </td>
              </tr>
              <tr>
                <td>
                  <span
                    v-if="stats.free_cancelings.court_rent > 0"
                    class="href"
                    @click="openFreeCancelingDetails('court_rent')"
                  >Аренда кортов</span>
                  <span v-else>Аренда кортов</span>
                </td>
                <td class="text-right">
                  <span
                    v-if="stats.free_cancelings.court_rent > 0"
                    class="href"
                    @click="openFreeCancelingDetails('court_rent')"
                  >{{ stats.free_cancelings.court_rent }}</span>
                  <span v-else>{{ stats.free_cancelings.court_rent }}</span>
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>Итого</td>
                <td class="text-right">
                  {{ stats.free_cancelings.total }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card>

      <!-- Блок 4: Штрафные отмены -->
      <v-card
        class="pa-4"
        :outlined="embedded"
      >
        <div class="text-subtitle-1 font-weight-bold mb-1">
          Штрафные отмены за период
        </div>
        <v-alert
          v-if="!stats.penalties.available_from"
          type="info"
          text
          dense
          class="mt-2 mb-3"
        >
          Данные пока не собраны — логирование штрафных отмен начнётся после деплоя.
        </v-alert>
        <v-alert
          v-else-if="noPenaltyDataForPeriod"
          type="warning"
          text
          dense
          class="mt-2 mb-3"
        >
          За выбранный период данных нет. Сбор начат с
          <strong>{{ formatDate(stats.penalties.available_from) }}</strong>.
        </v-alert>
        <template v-else>
          <div class="text--secondary text-body-2 mb-3">
            Данные доступны с {{ formatDate(stats.penalties.available_from) }}
          </div>
        </template>

        <v-row>
          <v-col
            v-for="section in penaltySections"
            :key="section.key"
            cols="12"
            md="6"
          >
            <div class="text-body-2 font-weight-medium mb-2">
              {{ section.label }}
            </div>
            <v-simple-table dense>
              <template #default>
                <thead>
                  <tr>
                    <th>Категория</th>
                    <th class="text-right">
                      Кол-во
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in penaltyTierRows"
                    :key="`${section.key}-${row.key}`"
                    :class="{ 'font-weight-bold': row.key === 'total' }"
                  >
                    <td>
                      <span
                        v-if="stats.penalties[section.key][row.key] > 0"
                        class="href"
                        @click="openPenaltyDetails(section.key, row.tier, stats.penalties[section.key][row.key])"
                      >{{ row.label }}</span>
                      <span v-else>{{ row.label }}</span>
                    </td>
                    <td class="text-right">
                      <span
                        v-if="stats.penalties[section.key][row.key] > 0"
                        class="href"
                        @click="openPenaltyDetails(section.key, row.tier, stats.penalties[section.key][row.key])"
                      >{{ stats.penalties[section.key][row.key] }}</span>
                      <span v-else>{{ stats.penalties[section.key][row.key] }}</span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>
      </v-card>
    </template>

    <div
      v-else-if="!loading && showEmptyHint"
      class="text--secondary text-body-2"
    >
      Нажмите «Применить» или выберите период для загрузки статистики
    </div>

    <!-- Модальное окно детализации -->
    <v-dialog
      v-model="detailDialog"
      max-width="800"
      scrollable
    >
      <v-card>
        <v-card-title class="text-h6">
          {{ detailTitle }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-progress-linear
            v-if="detailLoading"
            indeterminate
            class="mb-4"
          />
          <div
            v-else-if="!detailItems.length"
            class="text--secondary"
          >
            Записей нет
          </div>
          <v-data-table
            v-else
            :headers="detailHeaders"
            :items="detailItems"
            :items-per-page="20"
            dense
            class="elevation-0"
          >
            <template #[`item.status_label`]="{ item }">
              <v-chip
                small
                dark
                class="font-weight-medium gift-status-chip"
                :class="giftStatusClass(item.status)"
              >
                {{ item.status_label }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error"
            @click="detailDialog = false"
          >
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from '@/api/api'
import {
  LOYALTY_TIER_CARDS,
  getPeriodLoyaltyBlock,
  getPeriodLoyaltyCounts,
  normalizeStatisticsResponse,
} from '@/views/club_player/loyaltyConstants'

const GIFT_DETAIL_HEADERS = [
  { text: 'Участник', value: 'user_name' },
  { text: 'Дата', value: 'date' },
  { text: 'Время', value: 'time' },
  { text: 'Статус', value: 'status_label' },
]

const FREE_CANCELING_HEADERS = [
  { text: 'Участник', value: 'user_name' },
  { text: 'Дата занятия', value: 'session_date' },
  { text: 'Тренер', value: 'staff_name' },
  { text: 'Дата отмены', value: 'date' },
  { text: 'Время отмены', value: 'time' },
]

const PENALTY_HEADERS = [
  { text: 'Участник', value: 'user_name' },
  { text: 'Дата занятия', value: 'session_date' },
  { text: 'Время занятия', value: 'session_time' },
  { text: 'Тренер', value: 'staff_name' },
  { text: 'Дата отмены', value: 'date' },
  { text: 'Время отмены', value: 'time' },
  { text: 'Штраф, ₽', value: 'penalty_amount' },
  { text: 'Категория', value: 'penalty_tier_label' },
]

export default {
  name: 'ClubPlayerPeriodStatistics',
  props: {
    userId: {
      type: [Number, String],
      default: null,
    },
    dateFrom: {
      type: String,
      default: null,
    },
    dateTo: {
      type: String,
      default: null,
    },
    singleClient: {
      type: Boolean,
      default: null,
    },
    /** Вложен в панель клиента — outlined-карточки */
    embedded: {
      type: Boolean,
      default: false,
    },
    /** Показывать подсказку при отсутствии данных (страница статистики) */
    showEmptyHint: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      stats: null,
      detailDialog: false,
      detailLoading: false,
      detailTitle: '',
      detailHeaders: [],
      detailItems: [],
      loyaltyTierCards: LOYALTY_TIER_CARDS,
      penaltySections: [
        { key: 'activity', label: 'Групповые занятия' },
        { key: 'court_rent', label: 'Аренда кортов' },
      ],
      penaltyTierRows: [
        { key: 'total', label: 'Итого', tier: null },
        { key: 'half_12_24', label: '12–24 ч до начала (50%)', tier: 'half_12_24' },
        { key: 'full_under_12', label: '< 12 ч до начала (100%)', tier: 'full_under_12' },
        { key: 'full_rescheduled', label: 'После переноса (100%)', tier: 'full_rescheduled' },
      ],
    }
  },
  computed: {
    isSingleClient() {
      if (this.singleClient != null) return this.singleClient
      return this.userId != null && this.userId !== ''
    },
    periodLoyaltyBlock() {
      return getPeriodLoyaltyBlock(this.stats)
    },
    periodLoyaltyCounts() {
      return getPeriodLoyaltyCounts(this.periodLoyaltyBlock)
    },
    noPenaltyDataForPeriod() {
      if (!this.stats || !this.stats.penalties.available_from) return false
      const s = this.stats.penalties
      return s.activity.total === 0 && s.court_rent.total === 0
    },
    fetchKey() {
      return `${this.userId}|${this.dateFrom}|${this.dateTo}`
    },
  },
  watch: {
    fetchKey: {
      immediate: true,
      handler() {
        this.fetchStatistics()
      },
    },
  },
  methods: {
    buildParams() {
      const params = {}
      if (this.dateFrom && this.dateTo) {
        params.date_from = this.dateFrom
        params.date_to = this.dateTo
      }
      if (this.userId != null && this.userId !== '') {
        params.user_id = this.userId
      }
      return params
    },

    async fetchStatistics() {
      this.loading = true
      try {
        const data = await api.getClubPlayerStatistics(this.buildParams())
        this.stats = normalizeStatisticsResponse(data)
        this.$emit('loaded', this.stats)
      } catch (e) {
        const msg = e?.response?.data?.error || 'Ошибка загрузки статистики'
        console.error(msg, e)
        this.stats = null
      } finally {
        this.loading = false
      }
    },

    headersForSection(baseHeaders) {
      if (this.isSingleClient) {
        return baseHeaders.filter(h => h.value !== 'user_name')
      }
      return baseHeaders
    },

    async fetchDetails(params) {
      this.detailLoading = true
      this.detailItems = []
      try {
        const data = await api.getClubPlayerStatisticsDetails({
          ...this.buildParams(),
          ...params,
        })
        this.detailTitle = data.title || 'Детализация'
        this.detailItems = (data.results || []).map(item => ({
          ...item,
          status_label: item.status_label || item.status,
        }))
      } catch (e) {
        const msg = e?.response?.data?.error || 'Ошибка загрузки детализации'
        console.error(msg, e)
      } finally {
        this.detailLoading = false
      }
    },

    openGiftDetails(item) {
      if (!item || !item.count) return
      this.detailHeaders = this.headersForSection(GIFT_DETAIL_HEADERS)
      this.detailTitle = item.title
      this.detailDialog = true
      this.fetchDetails({ section: 'gifts', gift_id: item.gift_id })
    },

    openFreeCancelingDetails(recordType) {
      const count = this.stats?.free_cancelings?.[recordType]
      if (!count) return
      const labels = {
        activity: 'Бесплатные отмены — групповые занятия',
        court_rent: 'Бесплатные отмены — аренда кортов',
      }
      this.detailHeaders = this.headersForSection(FREE_CANCELING_HEADERS)
      this.detailTitle = labels[recordType] || 'Бесплатные отмены'
      this.detailDialog = true
      this.fetchDetails({ section: 'free_cancelings', record_type: recordType })
    },

    openPenaltyDetails(recordType, penaltyTier, count) {
      if (!count) return
      const sectionLabel = this.penaltySections.find(s => s.key === recordType)?.label || ''
      const tierLabel = this.penaltyTierRows.find(r => r.tier === penaltyTier)?.label || 'Итого'
      this.detailHeaders = this.headersForSection(PENALTY_HEADERS)
      this.detailTitle = `Штрафные отмены — ${sectionLabel} — ${tierLabel}`
      this.detailDialog = true
      const params = { section: 'penalties', record_type: recordType }
      if (penaltyTier) params.penalty_tier = penaltyTier
      this.fetchDetails(params)
    },

    formatDate(isoDate) {
      if (!isoDate) return '—'
      const [y, m, d] = isoDate.split('-')
      return `${d}.${m}.${y}`
    },

    giftStatusClass(status) {
      const map = {
        CREATED: 'gift-status-chip--created',
        PROCESSING: 'gift-status-chip--processing',
        COMPLETED: 'gift-status-chip--completed',
        CANCELED: 'gift-status-chip--canceled',
      }
      return map[status] || 'gift-status-chip--unknown'
    },

    clientsFilterRoute(tierKey) {
      return {
        path: '/club_player/clients',
        query: { loyalty: tierKey },
      }
    },
  },
}
</script>

<style scoped>
.href {
  text-decoration: underline;
  color: #2f65ae;
  cursor: pointer;
}

.href:hover {
  color: #1e4a8a;
}

.loyalty-tier-count--platinum {
  color: #8b9aab;
}

.loyalty-tier-count--gold {
  color: #c9a227;
}

.loyalty-tier-count--silver {
  color: #9aa0a6;
}

.loyalty-tier-count--bronze {
  color: #a67c52;
}

.loyalty-tier-link {
  display: inline-block;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.loyalty-tier-link:hover {
  opacity: 0.85;
}

.gift-status-chip {
  color: #fff !important;
}

.gift-status-chip--created {
  background-color: #2196F3 !important;
}

.gift-status-chip--processing {
  background-color: #E53935 !important;
}

.gift-status-chip--completed {
  background-color: #43A047 !important;
}

.gift-status-chip--canceled,
.gift-status-chip--unknown {
  background-color: #9E9E9E !important;
}
</style>
